/**
 * components/landing/home/PlatformOverview.jsx — Section 3.
 *
 * Interactive architecture:
 *   • A central "Kalki Ai Cloud Core" node.
 *   • 9 orbiting module nodes.
 *   • Selecting (click / hover) any module updates the right panel with
 *     description, benefits, a mock screenshot, and related features.
 *
 * Built entirely from CSS + SVG so it stays fast on mobile.  On viewports
 * below md, we swap the ring for a vertical list so the interaction still
 * works with touch.
 */

import { useState } from 'react'
import clsx from 'clsx'
import { Container, SectionHeading, Eyebrow } from '../design-system'
import { MockDashboardChrome, MiniSparkline, seededWalk } from './_shared'
import {
  Layers, Cpu, BarChart3, Wand2, GraduationCap, LineChart, CreditCard, Workflow, Building2,
} from 'lucide-react'

const MODULES = [
  {
    id: 'ai',       icon: Cpu,          title: 'AI Mentor',
    desc: 'RAG-backed AI assistant grounded in your knowledge base.  Cites sources, scores confidence, and auto-generates documentation.',
    benefits: ['Streaming answers', 'Citations + confidence', 'Follow-up chips'],
    related: ['Auto-Docs', 'Knowledge Base', 'Governance'],
    preview: 'ai',
  },
  {
    id: 'trading',  icon: BarChart3,    title: 'Trading Terminal',
    desc: 'Sub-second execution across CEXes/DEXes with idempotent orders, retries, and full reconciliation.',
    benefits: ['Multi-exchange routing', 'Idempotent orders', 'Risk guardrails'],
    related: ['Risk Engine', 'Order Router', 'Scanner'],
    preview: 'trading',
  },
  {
    id: 'strategy', icon: Wand2,        title: 'Strategy Builder',
    desc: 'Visual builder + code IDE.  Backtest, simulate, and deploy from the same runtime.',
    benefits: ['No-code + code', 'Live paper mode', 'One-click deploy'],
    related: ['Backtesting', 'Runtime', 'Templates'],
    preview: 'strategy',
  },
  {
    id: 'backtest', icon: LineChart,    title: 'Backtesting',
    desc: 'Full historical replay with slippage, commission, and Monte-Carlo robustness testing.',
    benefits: ['Equity + drawdown', 'Trade distribution', 'Robustness'],
    related: ['Strategy Builder', 'Reports', 'Portfolio Sim'],
    preview: 'backtest',
  },
  {
    id: 'academy',  icon: GraduationCap,title: 'Academy',
    desc: 'Built-in learning platform with courses, assessments, certificates, and community.',
    benefits: ['Adaptive path', 'Assessments', 'Certificates'],
    related: ['Community', 'Certifications', 'Content'],
    preview: 'academy',
  },
  {
    id: 'analytics',icon: BarChart3,    title: 'Analytics',
    desc: 'Per-tenant KPIs, platform-wide roll-ups, LLM usage attribution, and a live compliance dashboard.',
    benefits: ['Per-tenant KPIs', 'Attribution', 'Compliance dash'],
    related: ['Billing', 'AI Mentor', 'Audit'],
    preview: 'analytics',
  },
  {
    id: 'billing',  icon: CreditCard,   title: 'Billing',
    desc: 'Stripe + NowPayments with per-tenant plans, coupons, add-ons, metering, and invoicing.',
    benefits: ['Stripe + Crypto', 'Metering', 'Invoices + coupons'],
    related: ['Subscriptions', 'Plans', 'Add-ons'],
    preview: 'billing',
  },
  {
    id: 'automation',icon: Workflow,    title: 'Automation',
    desc: 'Composable workflows for onboarding, KYC, risk approval, and alert routing.',
    benefits: ['Workflow builder', 'Approvals', 'Alerts'],
    related: ['Notifications', 'Approvals', 'Webhooks'],
    preview: 'automation',
  },
  {
    id: 'wl',       icon: Building2,    title: 'White-Label',
    desc: 'Every pixel themable per tenant.  Custom domain, brand tokens, and SSO in minutes.',
    benefits: ['Brand tokens', 'Custom domain', 'SSO'],
    related: ['Tenants', 'DNS', 'Auth'],
    preview: 'wl',
  },
]

const RING_POSITIONS = [
  { x: 50, y:  6 }, { x: 84, y: 18 }, { x: 96, y: 50 },
  { x: 84, y: 82 }, { x: 50, y: 94 }, { x: 16, y: 82 },
  { x:  4, y: 50 }, { x: 16, y: 18 }, { x: 34, y: 0 },
]

const PlatformOverview = () => {
  const [active, setActive] = useState('ai')
  const mod = MODULES.find((m) => m.id === active) || MODULES[0]

  return (
    <section className="py-24 md:py-40">
      <Container>
        <SectionHeading
          eyebrow="Platform Overview"
          title={<>One platform.<br /><span className="text-zinc-400">Complete AI trading infrastructure.</span></>}
          subtitle="Every module below runs from a single, multi-tenant Kalki Ai Cloud core.  Hover any module to explore how it fits together."
          align="center"
          className="items-center mx-auto"
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-[1.1fr,1.2fr] items-start">
          {/* ─── Architecture ring / list ────────────────────────────── */}
          {/* Desktop ring */}
          <div className="relative aspect-square max-w-[560px] mx-auto w-full hidden md:block">
            {/* rings */}
            <div className="absolute inset-[6%] rounded-full border border-white/5" />
            <div className="absolute inset-[16%] rounded-full border border-white/[0.04]" />
            <div className="absolute inset-[28%] rounded-full border border-white/[0.03]" />

            {/* SVG connector lines */}
            <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
              {RING_POSITIONS.map((p, i) => (
                <line key={i} x1="50" y1="50" x2={p.x} y2={p.y}
                  stroke={MODULES[i].id === active ? '#FFC107' : 'rgba(255,255,255,0.06)'}
                  strokeWidth={MODULES[i].id === active ? '0.35' : '0.18'}
                  strokeDasharray={MODULES[i].id === active ? '0.6,0.4' : ''}
                />
              ))}
            </svg>

            {/* Core node */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-32 w-32 rounded-full border border-brand-gold/25 bg-black/70 backdrop-blur flex items-center justify-center text-center px-3 shadow-[0_0_60px_-10px_rgba(255,193,7,0.5)]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand-gold">Core</div>
                <div className="text-lg font-semibold text-white tracking-tight leading-tight mt-1">Kalki Ai Cloud<span className="text-brand-gold">.</span>Cloud</div>
              </div>
            </div>

            {/* Orbiting modules */}
            {MODULES.map((m, i) => {
              const p = RING_POSITIONS[i]
              const isActive = m.id === active
              return (
                <button
                  key={m.id}
                  onMouseEnter={() => setActive(m.id)}
                  onFocus={() => setActive(m.id)}
                  onClick={() => setActive(m.id)}
                  aria-label={m.title}
                  className={clsx(
                    'absolute h-14 w-14 rounded-2xl border backdrop-blur flex items-center justify-center transition-all duration-200 -translate-x-1/2 -translate-y-1/2',
                    isActive ? 'border-brand-gold/60 bg-brand-gold/15 text-brand-gold scale-110 shadow-[0_0_40px_-5px_rgba(255,193,7,0.7)]' : 'border-white/10 bg-white/[0.03] text-zinc-300 hover:border-brand-gold/40 hover:text-white',
                  )}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                >
                  <m.icon className="h-5 w-5" />
                </button>
              )
            })}
          </div>

          {/* Mobile module list */}
          <div className="grid grid-cols-2 gap-2 md:hidden">
            {MODULES.map((m) => {
              const isActive = m.id === active
              return (
                <button key={m.id} onClick={() => setActive(m.id)} className={clsx(
                  'flex items-center gap-2 rounded-xl border px-3 py-3 transition-colors',
                  isActive ? 'border-brand-gold/50 bg-brand-gold/10 text-brand-gold' : 'border-white/10 bg-white/[0.03] text-zinc-300',
                )}>
                  <m.icon className="h-4 w-4" />
                  <span className="text-[13px] font-medium">{m.title}</span>
                </button>
              )
            })}
          </div>

          {/* ─── Detail panel ─────────────────────────────────────── */}
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl border border-brand-gold/30 bg-brand-gold/10 text-brand-gold flex items-center justify-center">
                <mod.icon className="h-5 w-5" />
              </div>
              <div>
                <Eyebrow tone="orange">Module</Eyebrow>
                <div className="text-2xl font-semibold text-white tracking-tight mt-1">{mod.title}</div>
              </div>
            </div>
            <p className="text-zinc-400 leading-relaxed max-w-lg">{mod.desc}</p>
            <div className="grid grid-cols-3 gap-2 max-w-lg">
              {mod.benefits.map((b) => (
                <div key={b} className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[12px] text-zinc-300">{b}</div>
              ))}
            </div>
            {/* Preview — module-specific mini screenshot */}
            <MockDashboardChrome className="mt-6">
              <ModulePreview id={mod.preview} />
            </MockDashboardChrome>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1 mt-1">Related</span>
              {mod.related.map((r) => (
                <span key={r} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] px-2.5 py-1 text-[11px] text-zinc-300">
                  <Layers className="h-3 w-3 text-brand-gold/70" />
                  {r}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

// A per-module micro preview — keeps each module feeling distinct while
// avoiding a giant screenshot payload.  Every preview here is deliberately
// abstract-but-plausible — no fake numbers dressed as real metrics.
const ModulePreview = ({ id }) => {
  const spark = seededWalk(id.charCodeAt(0), 50, 6, 100)
  if (id === 'ai') return (
    <div className="p-4 space-y-2 min-h-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">AI Mentor · conversation</div>
      <div className="rounded-lg bg-white/[0.03] px-3 py-2 text-[12px] text-zinc-300">What&apos;s the current bias on ETH/USDT?</div>
      <div className="rounded-lg bg-brand-gold/[0.08] border border-brand-gold/20 px-3 py-2 text-[12px] text-zinc-200 leading-relaxed">
        ETH is compressing under 3.5k resistance.  If volume expands, expect breakout continuation.  <span className="text-brand-gold">Confidence 68%</span>.
      </div>
      <div className="text-[10px] font-mono text-zinc-500">Cites: HTF bias · volume profile · order flow</div>
    </div>
  )
  if (id === 'trading') return (
    <div className="p-4 space-y-3 min-h-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Order Ticket</div>
      <div className="grid grid-cols-2 gap-2">
        {['Symbol','Type','Side','Size','Price','TIF'].map((l, i) => (
          <div key={l} className="rounded bg-white/[0.03] border border-white/5 px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className="text-[12px] text-white">{['BTC-USDT','LIMIT','BUY','0.25','63,420','GTC'][i]}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="flex-1 rounded-lg bg-brand-emerald/15 text-brand-emerald text-[11px] text-center py-2 font-semibold">BUY</div>
        <div className="flex-1 rounded-lg bg-white/[0.03] text-zinc-500 text-[11px] text-center py-2">SELL</div>
      </div>
    </div>
  )
  if (id === 'strategy') return (
    <div className="p-4 min-h-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Strategy · SMC Trend</div>
      <div className="flex items-center gap-1 text-[11px] flex-wrap">
        {['Trigger','Filter','Confirm','Entry','Manage','Exit'].map((s, i) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span className={clsx('rounded-full border px-2 py-0.5', i < 3 ? 'border-brand-gold/40 text-brand-gold bg-brand-gold/10' : 'border-white/10 text-zinc-300')}>{s}</span>
            {i < 5 && <span className="text-zinc-600">→</span>}
          </span>
        ))}
      </div>
      <MiniSparkline data={spark} stroke="#FFC107" height={80} width={520} className="mt-3 w-full" />
    </div>
  )
  if (id === 'backtest') return (
    <div className="p-4 min-h-[180px] grid grid-cols-3 gap-3">
      <div className="col-span-2 rounded-lg bg-white/[0.02] p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Equity curve</div>
        <MiniSparkline data={spark} stroke="#10B981" height={120} width={420} className="w-full" />
      </div>
      <div className="space-y-2 text-[11px]">
        {[['Win-rate','61.4%'],['Profit factor','1.83'],['Max DD','-6.4%'],['Sharpe','1.42']].map(([k,v]) => (
          <div key={k} className="flex items-center justify-between rounded bg-white/[0.02] px-2 py-1.5">
            <span className="text-zinc-500">{k}</span>
            <span className="text-white font-mono">{v}</span>
          </div>
        ))}
      </div>
    </div>
  )
  if (id === 'academy') return (
    <div className="p-4 space-y-2 min-h-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Learning path · SMC Foundations</div>
      {['Structure','Order Blocks','Liquidity','Risk','Assessment'].map((c, i) => (
        <div key={c} className="flex items-center gap-3 rounded-lg bg-white/[0.02] px-3 py-2">
          <div className={clsx('h-5 w-5 rounded-full flex items-center justify-center text-[10px]', i < 3 ? 'bg-brand-emerald/20 text-brand-emerald' : 'bg-white/[0.05] text-zinc-500')}>{i < 3 ? '✓' : i + 1}</div>
          <div className="text-[12px] text-white flex-1">{c}</div>
          <div className="text-[10px] text-zinc-500">{i < 3 ? 'Complete' : `${(i - 3) * 40 + 60} min`}</div>
        </div>
      ))}
    </div>
  )
  if (id === 'analytics' || id === 'billing') return (
    <div className="p-4 min-h-[180px] grid grid-cols-4 gap-2">
      {[['MRR','$18,420','+4.2%'],['Tenants','12','+2'],['LLM tokens','1.4M','⇡'],['Success rate','99.4%','+0.1']].map(([l,v,d], i) => (
        <div key={l} className="rounded bg-white/[0.02] px-2 py-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
          <div className="text-white text-sm mt-0.5">{v}</div>
          <div className={clsx('text-[10px] font-mono', i === 0 || i === 1 || i === 3 ? 'text-brand-emerald' : 'text-brand-gold')}>{d}</div>
        </div>
      ))}
      <div className="col-span-4 rounded bg-white/[0.02] p-2">
        <MiniSparkline data={spark} stroke="#FFC107" height={80} width={520} className="w-full" />
      </div>
    </div>
  )
  if (id === 'automation') return (
    <div className="p-4 min-h-[180px] space-y-2 text-[11px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Workflow · Onboarding</div>
      {['New tenant created','KYC review','Assign plan','Provision workspace','Send welcome'].map((s, i) => (
        <div key={s} className="flex items-center gap-3">
          <div className={clsx('h-5 w-5 rounded-full flex items-center justify-center text-[10px]', i < 2 ? 'bg-brand-emerald/20 text-brand-emerald' : i === 2 ? 'bg-brand-gold/20 text-brand-gold animate-pulse' : 'bg-white/[0.05] text-zinc-500')}>
            {i < 2 ? '✓' : i === 2 ? '▶' : i + 1}
          </div>
          <div className="text-white">{s}</div>
        </div>
      ))}
    </div>
  )
  if (id === 'wl') return (
    <div className="p-4 min-h-[180px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Brand tokens</div>
      <div className="grid grid-cols-4 gap-2 text-[10px] text-zinc-400">
        <div><div className="h-10 rounded bg-brand-gold" /><div className="mt-1 font-mono">primary</div></div>
        <div><div className="h-10 rounded bg-brand-emerald" /><div className="mt-1 font-mono">success</div></div>
        <div><div className="h-10 rounded bg-zinc-100" /><div className="mt-1 font-mono">text</div></div>
        <div><div className="h-10 rounded bg-black border border-white/10" /><div className="mt-1 font-mono">bg</div></div>
      </div>
      <div className="mt-3 rounded bg-white/[0.02] p-2 text-[11px] font-mono text-zinc-400">domain: platform.yourbrand.com</div>
    </div>
  )
  return null
}

export default PlatformOverview
